import { useRef, useCallback } from 'react';

/**
 * A hook intended to assist in improving the accessibility of compound
 * components by capturing descendant child information in a context object.
 * Reach UI beautifully and concisely describes the problem `useCollection` has
 * been created to solve.
 *
 * @see https://github.com/reach/reach-ui/tree/43f450db7bcb25a743121fe31355f2294065a049/packages/descendants
 *
 * Approach:
 * `useCollection` returns a set of functions to create and query for data
 * relating to a "collection" of related items.
 *
 * `registerItem` will push data for a given item to a centralized data
 * structure that can then be queried by the provided `get*` functions.
 * `registerItem` should be called when a descendant child is mounted and
 * returned when unmounted to "unregister" the item.
 *
 * Usage:
 * ```
 * const List = props => {
 *   const { getItemCount, getItem } = useCollection()
 *
 *   React.useEffect(() => {
 *     console.log(getItemCount()) // 3
 *     console.log(getItemByIndex(1)) // { ref: { current: "..."}, id: "two" }
 *   }, [])
 *
 *   return <div>{props.children}</div>
 * }
 *
 * const ListItem = props => {
 *   const { registerItem } = useCollection()
 *   const ref = React.useRef(null)
 *
 *   React.useEffect(() => return registerItem({ ref, id: props.id }))
 *
 *   return <div ref={ref} id={id}>{props.children}</div>
 * }
 *
 * const MyApp = () => {
 *   return (
 *     <List>
 *       <ListItem id="one">Item one</ListItem>
 *       <ListItem id="two">Item two</ListItem>
 *       <ListItem id="three">Item three</ListItem>
 *     </List>
 *   )
 * }
 * ```
 */

const useCollection = () => {
  const itemsRef = useRef([]);
  const getItemCount = useCallback(() => {
    return itemsRef.current.length;
  }, []);
  const getAllItems = useCallback(() => {
    return itemsRef.current;
  }, []);
  const getItemById = useCallback(id => {
    return itemsRef.current.find(item => item.id === id);
  }, []);
  const getItemByIndex = useCallback(index => {
    return itemsRef.current[index];
  }, []);
  const getItemIndexById = useCallback(id => {
    return itemsRef.current.findIndex(item => item.id === id);
  }, []);
  const getItemsByPredicate = useCallback(predicate => {
    return itemsRef.current.filter(predicate);
  }, []);
  const registerItem = useCallback(item => {
    itemsRef.current = [...itemsRef.current, item].sort((a, b) => {
      if (!a.ref.current || !b.ref.current) {
        return 0;
      }
      return sortItemsByDocumentPosition(a.ref.current, b.ref.current);
    });
    return () => {
      return itemsRef.current = itemsRef.current.filter(currItem => currItem.id !== item.id);
    };
  }, []);
  return {
    getItemCount,
    getAllItems,
    getItemById,
    getItemByIndex,
    getItemIndexById,
    getItemsByPredicate,
    registerItem
  };
};
function sortItemsByDocumentPosition(a, b) {
  const position = a.compareDocumentPosition(b);

  // eslint-disable-next-line no-bitwise
  if (position & Node.DOCUMENT_POSITION_FOLLOWING) {
    return -1;
  }

  // eslint-disable-next-line no-bitwise
  if (position & Node.DOCUMENT_POSITION_PRECEDING) {
    return 1;
  }
  return 0;
}

export { useCollection };
